import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { container } from 'tsyringe';
import { FullChatMessage, PTChatMessage, PTChatPeople } from '../../../models/ptChat';
import { ChatService } from '../../../services/chat-service';
import { ErrorService } from '../../../services/error-service';
import { PTApiService } from '../../../services/ptapi-service';
import { compare, sleep, useSharedState } from '../../../utils/helpers';
import { ChatMsg } from './HostChatTool';
import "./PrivateChatTool.scss";
import { ChatMessageInput } from './ChatMessageInput';
import SearchBox from '../../search/SearchBox';
import { PeopleService } from '../../../services/people-service';
import { People } from '../../../models/peopleClass';
import { ptEventDetails, ptPublicEvent } from '../../../models/ptEvent';

const scrollToRef = (ref) => {
    if (ref && ref.current) {
        ref.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }
};

const getAttendeeGuidFromMessage = (msg: FullChatMessage | PTChatMessage) => msg.tag === 'moderator_reply' ? msg.receiver?.guid : msg.sender?.guid;
const getAttendeeNameFromMessage = (msg: FullChatMessage | PTChatMessage) => msg.tag === 'moderator_reply' ? msg.receiver?.name : msg.sender?.name;
const enrichMessage = (msg: FullChatMessage | PTChatMessage) => {
    const freeMsg = msg as any;
    freeMsg.isSelf = msg.tag === 'moderator_reply';
    freeMsg.date = moment.unix(msg.timestamp / 1000).format('LT');
    freeMsg.fromNow = moment.unix(msg.timestamp / 1000).fromNow();
    return freeMsg;
};

interface PrivateChatRecipientListItem {
    endpointGuid: string;
    name: string;
    lastMessage?: PTChatMessage;
}

export const PrivateChatTool = () => {

    const [ptApiService] = useState(container.resolve(PTApiService));
    const [chatService] = useState(container.resolve(ChatService));
    const [errorService] = useState(container.resolve(ErrorService));
    const [peopleService] = useState(container.resolve(PeopleService));
    const [recipientsList, setRecipientsList] = useState<PrivateChatRecipientListItem[]>([]);
    const [filteredRecipientsList, setFilteredRecipientsList] = useState<PrivateChatRecipientListItem[]>([]);
    const [lastPrivateMessages, setLastPrivateMessages] = useState<PTChatMessage[]>([]);
    const [showWelcome, setShowWelcome] = useState(true);
    // Recipient endpoint guid
    const [currentConversation, setCurrentConversation] = useState<PTChatPeople>(null);
    const [currentConversationMessages, setCurrentConversationMessages] = useState<PTChatMessage[]>([]);
    const [searchText, setSearchText] = useState('');
    const [people] = useSharedState(peopleService.peoples$) as [People[]];
    const [socketChatMessages] = useSharedState(chatService.privateChatMessages$) as [FullChatMessage[]];
    const [currentEvent] = useSharedState(ptApiService.ptCurrentEventInformation$) as [[ptPublicEvent, ptEventDetails]];
    const [areLastPrivateMessagesLoaded, setAreLastPrivateMessagesLoaded] = useState(false);
    const lastMsg = useRef(null);

    const init = async () => {
        const messages = []; // await ptApiService.getLastPrivateMessages();
        setLastPrivateMessages(messages);
        setShowWelcome(messages && messages.length === 0);
        setAreLastPrivateMessagesLoaded(true);
    };

    const updateLastMessages = useCallback((msgs: FullChatMessage[]) => {
        for (const msg of msgs) {
            const existingMsg = lastPrivateMessages?.find(m => m.id === msg.id);

            if (existingMsg) {
                continue;
            }

            const attendeeGuid = getAttendeeGuidFromMessage(msg);
            const existingConversation = lastPrivateMessages.find(c => attendeeGuid === getAttendeeGuidFromMessage(c));
            if (!existingConversation) {
                setLastPrivateMessages([msg, ...lastPrivateMessages]);
            } else {
                setLastPrivateMessages([msg, ...(lastPrivateMessages.filter(c => c.id !== existingConversation.id))]);
            }
            setShowWelcome(false);
        }
    }, [lastPrivateMessages]);

    const updateCurrentConversation = useCallback((msgs: FullChatMessage[]) => {
        if (!currentConversation || !currentConversationMessages) {
            return;
        }

        for (const msg of msgs) {
            const attendeeGuid = getAttendeeGuidFromMessage(msg);
            if (attendeeGuid === currentConversation.guid && !currentConversationMessages.find(m => m.id === msg.id)) {
                msg.isUnread = false;
                setCurrentConversationMessages([...currentConversationMessages, msg]);
            }
        }

        setTimeout(() => scrollToRef(lastMsg), 20);
    }, [currentConversationMessages, currentConversation]);

    useEffect(() => {
        init();
    }, []);

    useEffect(() => {
        if (!socketChatMessages || socketChatMessages.length === 0) {
            return;
        }
        const richMessages = socketChatMessages.map(m => enrichMessage(m));
        updateCurrentConversation(richMessages);
        updateLastMessages(richMessages);
    }, [socketChatMessages]);

    useEffect(() => {
        const recipients: PrivateChatRecipientListItem[] = [];
        for (const m of lastPrivateMessages) {
            const attendeeEndpointGuid = getAttendeeGuidFromMessage(m);
            if (!recipients.find(r => r.endpointGuid === attendeeEndpointGuid)) {
                recipients.push({
                    name: getAttendeeNameFromMessage(m),
                    endpointGuid: attendeeEndpointGuid,
                    lastMessage: m
                });
            }
        }

        for (const p of people) {
            if (!p.isSelf && !recipients.find(r => r.endpointGuid === p.endpointGUID)) {
                // If one2OneModChat is disabled, do not add attendees in the list
                if (!currentEvent[1].one2OneModChat && p.role === 'Attendee') {
                    continue;
                }
                recipients.push({
                    name: p.name,
                    endpointGuid: p.endpointGUID
                });
            }
        }


        setRecipientsList(recipients);
    }, [people, lastPrivateMessages, currentEvent]);

    useEffect(() => {
        if (!searchText) {
            setFilteredRecipientsList(recipientsList);
        } else {
            const lowerCasedSearch = searchText.trim().toLowerCase();
            setFilteredRecipientsList(
                recipientsList.filter(r => r.name?.toLowerCase().indexOf(lowerCasedSearch) > -1)
            );
        }
    }, [searchText, recipientsList]);

    useEffect(() => {
        if (chatService.currentOneToOneEndpointGuid$.value && recipientsList && recipientsList.length > 0 && areLastPrivateMessagesLoaded) {
            const conv = recipientsList.find(r => r.endpointGuid === chatService.currentOneToOneEndpointGuid$.value);
            chatService.currentOneToOneEndpointGuid$.next(null);
            if (conv) {
                setShowWelcome(false);
                loadConversation(conv);
            }
        }
    }, [recipientsList, areLastPrivateMessagesLoaded]);

    const loadConversation = async (r: PrivateChatRecipientListItem) => {
        setCurrentConversationMessages([]);
        setCurrentConversation({
            guid: r.endpointGuid,
            name: r.name,
            role: '',
            userId: 0
        });
        if (!r.lastMessage) {
            return;
        }
        try {
            setCurrentConversationMessages(
                (await ptApiService.getGeneralChatHistory(true, r.endpointGuid))
                    .sort((a, b) => compare(a.timestamp, b.timestamp, true))
                    .map(m => enrichMessage(m))
            );
        } catch (e) {
            console.error(e);
            errorService.throwError(e);
        }

        // Mark the message as read
        delete (r.lastMessage as any)?.isUnread;

        await sleep(20);
        scrollToRef(lastMsg);
    };

    const onSendMessage = useCallback(async (msg: string) => {
        try {
            await chatService.sendChatMessage(msg, 'private', { guid: currentConversation.guid, name: currentConversation.name, role: 'attendee' });
        } catch (e) {
            console.error(e);
            errorService.throwError(e);
        }
    }, [currentConversation]);

    const onSearchUpdate = (search: string) => {
        setSearchText(search);
    };

    return <div className="private-chat-tool-container">
        {showWelcome && <div className='hostchattool-welcome'>
            <div className='hostchattool-welcome-img hostchattool-welcome-img-private'></div>
            <div className='hostchattool-welcome-title'>Welcome to Direct Messages!</div>
            <div className='hostchattool-welcome-tips'>Use this chat to talk directly with 1 to 1.</div>
            <div className='hostchattool-welcome-btn'>
                <button className='btn-with-gradient' onClick={() => setShowWelcome(false)}>START</button>
            </div>
        </div>}
        {!showWelcome && <div className="private-chat-tool-inner-container">
            <div className="private-chat-tool-header">
                <div className="title">Direct Messages</div>
                {!currentConversation && <SearchBox text={searchText} placeholder={'Search...'} onChangeText={onSearchUpdate}></SearchBox>}
            </div>
            {!currentConversation && <div className="private-chat-tool-body">
                <div className="private-chat-conversations-list">
                    {filteredRecipientsList.map(m => <div key={m.endpointGuid} className="private-chat-conversation-row" onClick={() => loadConversation(m)}>
                        <div className="unread-count-col">
                            {(m.lastMessage as any)?.isUnread && <div className="studio-colored-tab-badge"></div>}
                        </div>
                        <div className="user-name-col">{m.name}</div>
                        <div className={`message-text-col ${m.lastMessage?.tag === 'moderator_reply' ? 'message-reply' : ''}`}>{m.lastMessage?.body}</div>
                        <div className="arrow-col">
                            <div className="arrow-icon">&rsaquo;</div>
                        </div>
                    </div>)}
                </div>
            </div>}
            {currentConversation && <div className="private-chat-tool-conversation-body">
                <div className="private-chat-tool-conversation-header" onClick={() => setCurrentConversation(null)}>
                    <div className="reversed-arrow-icon">&rsaquo;</div>
                    <div className="conversation-recipient-name">{currentConversation.name}</div>
                </div>
                <div className="private-chat-tool-conversation-wrapper">
                    {/* {currentConversationMessages.length === 0 && <div>Loading conversation...</div>} */}
                    {currentConversationMessages.length > 0 && <div className="private-chat-tool-conversation hostchattool-wrapper">
                        <ul>
                            {currentConversationMessages.map(c => <li key={c.id}>
                                <ChatMsg msg={c}></ChatMsg>
                            </li>)}
                            <li ref={lastMsg} id="dummy_for_scrollIntoView" className="hostchattool-last-message"></li>
                        </ul>
                    </div>}
                </div>
                <ChatMessageInput onSendMessage={(msg) => onSendMessage(msg)} clearInput={false} placeHolder={'Chat privately with your co-hosts…'}/>
            </div>}

        </div>}
    </div>;
}